import MoreVertIcon from '@mui/icons-material/MoreVert'
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { useState } from 'react'
import type React from 'react'
import { rateLabel } from 'Pages/Attendance/attendanceDashboardFormatters'
import { AttendanceRecordDrawer } from 'Pages/Attendance/AttendanceRecordDrawer'
import { AttendanceStatusButton } from 'Pages/Attendance/AttendanceStatusButton'
import { AttendanceStatusChip } from 'Pages/Attendance/AttendanceStatusChip'
import {
  type AttendanceCellDto,
  type AttendanceDateColumnDto,
  type AttendanceRecordDraftUpdate,
  type AttendanceRecordStatus,
  type AttendanceRosterRowDto
} from 'Pages/Attendance/types'

type CellAction = 'clear' | AttendanceRecordStatus
type SortColumn = 'attendanceRate' | 'tardyRate'
type SortDirection = 'asc' | 'desc'

export interface AttendanceSessionTableProps {
  dates: AttendanceDateColumnDto[]
  rows: AttendanceRosterRowDto[]
  currentDate?: string
  savingCells?: Set<string>
  bulkActionsDisabled?: boolean
  onStatusChange: (row: AttendanceRosterRowDto, cell: AttendanceCellDto, status: AttendanceRecordStatus, note?: string | null) => void
  onRecordChangesSave: (row: AttendanceRosterRowDto, updates: AttendanceRecordDraftUpdate[]) => boolean
  onClear: (row: AttendanceRosterRowDto, cell: AttendanceCellDto) => void
  onBulkStatusChange: (date: AttendanceDateColumnDto, status: AttendanceRecordStatus, cells: Array<{ row: AttendanceRosterRowDto, cell: AttendanceCellDto }>) => void
  onBulkClear: (date: AttendanceDateColumnDto, cells: Array<{ row: AttendanceRosterRowDto, cell: AttendanceCellDto }>) => void
  onMarkNotInSession?: (date: AttendanceDateColumnDto, reason: string) => void
  onClearNotInSession?: (date: AttendanceDateColumnDto) => void
}

const statuses: AttendanceRecordStatus[] = ['Present', 'Tardy', 'Absent']
const attendanceFontFamily = 'Quicksand, sans-serif'
const textPrimary = '#595959'
const textSecondary = '#8F8F8F'
const textMuted = '#8F8F8F'
const studentColumnWidth = 181
const attendanceRateColumnWidth = 103
const tardyColumnWidth = 95
const dateColumnWidth = 137
const notInSessionReasonMaxLength = 1000

export function AttendanceSessionTable (props: AttendanceSessionTableProps): JSX.Element {
  const [cellMenu, setCellMenu] = useState<{ anchor: HTMLElement, row: AttendanceRosterRowDto, cell: AttendanceCellDto } | null>(null)
  const [bulkMenu, setBulkMenu] = useState<{ anchor: HTMLElement, date: AttendanceDateColumnDto } | null>(null)
  const [notInSessionDialog, setNotInSessionDialog] = useState<{ date: AttendanceDateColumnDto, mode: 'mark' | 'edit' } | null>(null)
  const [notInSessionReason, setNotInSessionReason] = useState('')
  const [confirmOlderNotInSessionDate, setConfirmOlderNotInSessionDate] = useState<AttendanceDateColumnDto | null>(null)
  const [recordDrawerRow, setRecordDrawerRow] = useState<AttendanceRosterRowDto | null>(null)
  const [sort, setSort] = useState<{ column: SortColumn, direction: SortDirection } | null>(null)

  const rows = sortedRows(props.rows, sort)
  const currentDate = props.currentDate ?? todayDateValue()
  const hasDateAction = props.dates.some(date =>
    date.canMarkNotInSession === true ||
    date.canClearNotInSession === true ||
    date.canEditNotInSessionReason === true ||
    date.showNotInSessionAction === true)
  const readOnly = !hasDateAction && rows.every(row => row.cells.every(cell => !cell.canEdit && !cell.canAddNote))
  const recordDrawerCurrentRow = recordDrawerRow == null
    ? null
    : props.rows.find(row => row.newEnrollmentId === recordDrawerRow.newEnrollmentId) ?? recordDrawerRow

  const closeCellMenu = (): void => { setCellMenu(null) }
  const closeBulkMenu = (): void => { setBulkMenu(null) }
  const closeNotInSessionDialog = (): void => {
    setNotInSessionDialog(null)
    setNotInSessionReason('')
  }

  const runCellAction = (action: CellAction): void => {
    if (cellMenu == null) return
    if (action === 'clear') {
      props.onClear(cellMenu.row, cellMenu.cell)
    } else {
      props.onStatusChange(cellMenu.row, cellMenu.cell, action)
    }
    closeCellMenu()
  }

  const runBulkAction = (action: CellAction): void => {
    if (bulkMenu == null) return
    if (action === 'clear') {
      const cells = clearableCellsForDate(props.rows, bulkMenu.date)
      if (cells.length === 0) return
      props.onBulkClear(bulkMenu.date, cells)
    } else {
      const cells = editableCellsForDate(props.rows, bulkMenu.date, action)
      if (cells.length === 0) return
      props.onBulkStatusChange(bulkMenu.date, action, cells)
    }
    closeBulkMenu()
  }

  const openNotInSessionDialog = (date: AttendanceDateColumnDto, mode: 'mark' | 'edit'): void => {
    setNotInSessionDialog({ date, mode })
    setNotInSessionReason(mode === 'edit' ? date.notInSessionReason ?? '' : '')
    closeBulkMenu()
  }

  const submitNotInSessionDialog = (): void => {
    if (notInSessionDialog == null || props.onMarkNotInSession == null || notInSessionReason.trim() === '') return

    if (notInSessionDialog.date.willLockNotInSessionAfterSubmit === true) {
      setConfirmOlderNotInSessionDate(notInSessionDialog.date)
      return
    }

    props.onMarkNotInSession(notInSessionDialog.date, notInSessionReason.trim())
    closeNotInSessionDialog()
  }

  const confirmOlderNotInSession = (): void => {
    if (confirmOlderNotInSessionDate == null || props.onMarkNotInSession == null || notInSessionReason.trim() === '') return

    props.onMarkNotInSession(confirmOlderNotInSessionDate, notInSessionReason.trim())
    setConfirmOlderNotInSessionDate(null)
    closeNotInSessionDialog()
  }

  const toggleSort = (column: SortColumn): void => {
    setSort(current => {
      if (current?.column !== column) return { column, direction: 'desc' }
      return { column, direction: current.direction === 'desc' ? 'asc' : 'desc' }
    })
  }

  return (
    <>
      <TableContainer
        data-testid="attendance-session-table"
        sx={{
          backgroundColor: 'background.paper',
          border: '1px solid rgba(89, 89, 89, 0.3)',
          borderRadius: '12px',
          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.05)',
          overflowX: 'auto',
          overflowY: 'hidden',
          '& *': {
            fontFamily: attendanceFontFamily
          }
        }}
      >
        <Table sx={{ minWidth: tableMinWidth(props.dates.length), tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: studentColumnWidth }} />
            <col style={{ width: attendanceRateColumnWidth }} />
            <col style={{ width: tardyColumnWidth }} />
            {props.dates.map(date => (
              <col key={dateKey(date.attendanceDate)} style={{ width: dateColumnWidth }} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              <HeaderCell sx={columnWidthSx(studentColumnWidth)}>Student Name</HeaderCell>
              <HeaderCell sx={columnWidthSx(attendanceRateColumnWidth)}>
                <SortableHeaderLabel
                  active={sort?.column === 'attendanceRate'}
                  direction={sort?.column === 'attendanceRate' ? sort.direction : 'desc'}
                  label="Attendance Rate"
                  onClick={() => { toggleSort('attendanceRate') }}
                />
              </HeaderCell>
              <HeaderCell sx={columnWidthSx(tardyColumnWidth)}>
                <SortableHeaderLabel
                  active={sort?.column === 'tardyRate'}
                  direction={sort?.column === 'tardyRate' ? sort.direction : 'desc'}
                  label="Tardy"
                  onClick={() => { toggleSort('tardyRate') }}
                />
              </HeaderCell>
              {props.dates.map(date => (
                <DateHeaderCell
                  key={dateKey(date.attendanceDate)}
                  bulkActionsDisabled={props.bulkActionsDisabled === true}
                  currentDate={currentDate}
                  date={date}
                  readOnly={readOnly}
                  rows={props.rows}
                  onBulkMenuOpen={anchor => { setBulkMenu({ anchor, date }) }}
                />
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIndex) => (
              <TableRow
                key={row.newEnrollmentId}
                data-testid={`attendance-row-${row.newEnrollmentId}`}
                sx={{ '&:last-child td': { borderBottom: 0 } }}
              >
                <TableCell sx={{ ...columnWidthSx(studentColumnWidth), borderColor: '#E6E6E6', py: 1.5, verticalAlign: 'middle' }}>
                  <Typography sx={{ color: textPrimary, fontSize: '0.875rem', fontWeight: 600 }}>{row.childName}</Typography>
                  <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
                    <Link
                      component="button"
                      type="button"
                      onClick={() => { setRecordDrawerRow(row) }}
                      sx={{ color: '#7136BC', fontFamily: attendanceFontFamily, fontSize: '0.75rem', fontWeight: 500 }}
                    >
                      Attendance Details
                    </Link>
                    {hasNoteCapableAbsenceWithoutNote(row) && (
                      <Tooltip title="Absence note can be added">
                        <Box
                          aria-label="Absence note can be added"
                          data-testid={`attendance-missing-note-callout-${row.newEnrollmentId}`}
                          role="img"
                          sx={{ alignItems: 'center', color: '#7136BC', display: 'inline-flex', opacity: 0.9 }}
                        >
                          <NoteAltOutlinedIcon sx={{ fontSize: 15 }} />
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <BodyCell align="center" sx={columnWidthSx(attendanceRateColumnWidth)}>
                  <Typography data-testid={`attendance-rate-${row.newEnrollmentId}`} sx={{ color: attendanceRateColor(row.attendanceRate), fontSize: '0.875rem', fontWeight: 500 }}>{rateLabel(row.attendanceRate)}</Typography>
                  {row.absenceCount > 0 && (
                    <Typography sx={{ color: textSecondary, fontSize: '0.75rem', fontWeight: 400 }}>{row.absenceCount} Absences</Typography>
                  )}
                </BodyCell>
                <BodyCell align="center" sx={columnWidthSx(tardyColumnWidth)}>
                  <Typography sx={{ color: textPrimary, fontSize: '0.875rem', fontWeight: 500 }}>{rateLabel(row.tardyRate)}</Typography>
                </BodyCell>
                {props.dates.map(date => {
                  if (date.isNotInSession === true) {
                    if (rowIndex > 0) return null

                    return (
                      <BodyCell
                        key={cellKey(row.newEnrollmentId, date.attendanceDate)}
                        align="center"
                        data-testid={`attendance-not-in-session-${dateKey(date.attendanceDate)}`}
                        rowSpan={rows.length}
                        sx={{
                          ...columnWidthSx(dateColumnWidth),
                          ...currentColumnSx(isCurrentDate(date.attendanceDate, currentDate)),
                          p: 0,
                          position: 'relative',
                          verticalAlign: 'middle'
                        }}
                      >
                        <NotInSessionDay reason={date.notInSessionReason} />
                      </BodyCell>
                    )
                  }

                  if (date.isScheduled === false) {
                    if (rowIndex > 0) return null

                    return (
                      <BodyCell
                        key={cellKey(row.newEnrollmentId, date.attendanceDate)}
                        align="center"
                        data-testid={`attendance-not-scheduled-${dateKey(date.attendanceDate)}`}
                        rowSpan={rows.length}
                        sx={{
                          ...columnWidthSx(dateColumnWidth),
                          ...currentColumnSx(isCurrentDate(date.attendanceDate, currentDate)),
                          p: 0,
                          position: 'relative',
                          verticalAlign: 'middle'
                        }}
                      >
                        <NotScheduledDay />
                      </BodyCell>
                    )
                  }

                  const cell = date.isInSession ? cellForDate(row, date.attendanceDate) : undefined
                  const key = cellKey(row.newEnrollmentId, date.attendanceDate)
                  const saving = props.savingCells?.has(key) ?? false
                  const isCurrent = isCurrentDate(date.attendanceDate, currentDate)
                  return (
                    <BodyCell
                      key={key}
                      align="center"
                      data-testid={`attendance-cell-${row.newEnrollmentId}-${dateKey(date.attendanceDate)}`}
                      sx={{
                        ...columnWidthSx(dateColumnWidth),
                        ...currentColumnSx(isCurrent),
                        opacity: cell?.canEdit === false && cell.status == null ? 0.55 : 1
                      }}
                    >
                      {cell == null
                        ? <AttendanceStatusChip status="NotScheduled" disabled />
                        : readOnly
                          ? <AttendanceStatusChip status={cell.status ?? 'Empty'} disabled />
                          : (
                            <AttendanceStatusButton
                              cell={cell}
                              disabled={!cell.canEdit || saving}
                              fullWidth
                              saving={saving}
                              tooltipTitle={cellTooltipTitle(cell, saving)}
                              onClick={event => { setCellMenu({ anchor: event.currentTarget, row, cell }) }}
                            />
                            )}
                    </BodyCell>
                  )
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu anchorEl={cellMenu?.anchor ?? null} open={cellMenu != null} onClose={closeCellMenu}>
        {statuses.map(status => (
          <MenuItem key={status} disabled={cellMenu?.cell.status === status} sx={menuItemSx} onClick={() => { runCellAction(status) }}>Mark {status}</MenuItem>
        ))}
        {cellMenu?.cell.status != null && cellMenu.cell.attendanceRecordId != null && cellMenu.cell.canEdit && (
          <MenuItem sx={menuItemSx} onClick={() => { runCellAction('clear') }}>Clear Response</MenuItem>
        )}
      </Menu>

      <Menu anchorEl={bulkMenu?.anchor ?? null} open={bulkMenu != null} onClose={closeBulkMenu}>
        {bulkMenu?.date.isNotInSession === true && (
          <MenuItem
            disabled={props.onClearNotInSession == null || bulkMenu.date.canClearNotInSession !== true}
            sx={menuItemSx}
            onClick={() => {
              if (bulkMenu == null || props.onClearNotInSession == null) return
              props.onClearNotInSession(bulkMenu.date)
              closeBulkMenu()
            }}
          >
            Mark Day as In Session
          </MenuItem>
        )}
        {bulkMenu?.date.isNotInSession === true && (
          <MenuItem
            disabled={props.onMarkNotInSession == null || bulkMenu.date.canEditNotInSessionReason !== true}
            sx={menuItemSx}
            onClick={() => {
              if (bulkMenu == null) return
              openNotInSessionDialog(bulkMenu.date, 'edit')
            }}
          >
            Edit Reason
          </MenuItem>
        )}
        {bulkMenu != null && bulkMenu.date.isNotInSession !== true && statuses.map(status => (
          <MenuItem key={status} disabled={editableCellsForDate(props.rows, bulkMenu.date, status).length === 0} sx={menuItemSx} onClick={() => { runBulkAction(status) }}>Mark All {status}</MenuItem>
        ))}
        {bulkMenu != null && bulkMenu.date.isNotInSession !== true && clearableCellsForDate(props.rows, bulkMenu.date).length > 0 && (
          <MenuItem sx={menuItemSx} onClick={() => { runBulkAction('clear') }}>Clear All Responses</MenuItem>
        )}
        {bulkMenu != null && bulkMenu.date.isNotInSession !== true && (
          <Tooltip title={bulkMenu.date.notInSessionActionDisabledReason ?? ''}>
            <span>
              <MenuItem
                disabled={props.onMarkNotInSession == null || bulkMenu.date.canMarkNotInSession !== true}
                sx={menuItemSx}
                onClick={() => { openNotInSessionDialog(bulkMenu.date, 'mark') }}
              >
                Mark Day as Not in Session
              </MenuItem>
            </span>
          </Tooltip>
        )}
      </Menu>

      <Dialog open={notInSessionDialog != null && confirmOlderNotInSessionDate == null} onClose={closeNotInSessionDialog}>
        <DialogTitle>{notInSessionDialog?.mode === 'edit' ? 'Edit Reason' : 'Mark Day as Not in Session'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: { xs: 280, sm: 420 }, pt: 1 }}>
          <DialogContentText>
            {notInSessionDialog?.mode === 'edit'
              ? 'Update the reason for this not-in-session day.'
              : 'Attendance entered for this day will not count while it is marked not in session.'}
          </DialogContentText>
          <TextField
            autoFocus
            label="Reason"
            multiline
            minRows={3}
            value={notInSessionReason}
            onChange={event => { setNotInSessionReason(event.target.value) }}
            inputProps={{ maxLength: notInSessionReasonMaxLength }}
            helperText={`${notInSessionReason.length}/${notInSessionReasonMaxLength}`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeNotInSessionDialog}>Cancel</Button>
          <Button variant="contained" disabled={notInSessionReason.trim() === ''} onClick={submitNotInSessionDialog}>
            {notInSessionDialog?.mode === 'edit' ? 'Save Reason' : 'Mark Day as Not in Session'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOlderNotInSessionDate != null} onClose={() => { setConfirmOlderNotInSessionDate(null) }}>
        <DialogTitle>Mark Older Day as Not in Session?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This day is outside the normal editing window. After it is marked not in session, existing attendance for this date will not count, providers will not be required to enter attendance, and the date will be excluded from attendance calculations. This change cannot be edited afterward.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirmOlderNotInSessionDate(null) }}>Cancel</Button>
          <Button variant="contained" onClick={confirmOlderNotInSession}>Mark Day as Not in Session</Button>
        </DialogActions>
      </Dialog>

      <AttendanceRecordDrawer
        open={recordDrawerRow != null}
        dates={props.dates}
        row={recordDrawerCurrentRow}
        savingCells={props.savingCells}
        onClose={() => { setRecordDrawerRow(null) }}
        onSaveChanges={(row, updates) => {
          if (props.onRecordChangesSave(row, updates)) {
            setRecordDrawerRow(null)
          }
        }}
      />
    </>
  )
}

function NotInSessionDay (props: { reason?: string | null }): JSX.Element {
  return (
    <Box
      sx={{
        alignItems: 'center',
        backgroundColor: '#F5F5F5',
        borderRadius: '8px',
        bottom: 12,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        left: 8,
        minHeight: 40,
        overflow: 'hidden',
        px: 1.5,
        position: 'absolute',
        right: 8,
        top: 12
      }}
    >
      <Typography
        sx={{
          color: textSecondary,
          fontFamily: attendanceFontFamily,
          fontSize: '0.75rem',
          fontWeight: 500,
          lineHeight: 1.25,
          textAlign: 'center'
        }}
      >
        Not in Session
      </Typography>
      {props.reason != null && props.reason.trim() !== '' && (
        <Typography
          data-testid="attendance-not-in-session-reason"
          title={props.reason}
          sx={{
            color: textSecondary,
            display: '-webkit-box',
            fontFamily: attendanceFontFamily,
            fontSize: '0.6875rem',
            fontWeight: 400,
            lineHeight: 1.2,
            maxWidth: '100%',
            mt: 0.5,
            overflow: 'hidden',
            overflowWrap: 'anywhere',
            textAlign: 'center',
            textOverflow: 'ellipsis',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 3,
            wordBreak: 'break-word'
          }}
        >
          {props.reason}
        </Typography>
      )}
    </Box>
  )
}

function NotScheduledDay (): JSX.Element {
  return (
    <Box
      sx={{
        alignItems: 'center',
        backgroundColor: '#FAFAFA',
        borderRadius: '8px',
        bottom: 12,
        boxSizing: 'border-box',
        display: 'flex',
        justifyContent: 'center',
        left: 8,
        minHeight: 40,
        px: 1.5,
        position: 'absolute',
        right: 8,
        top: 12
      }}
    >
      <Typography sx={{ color: textSecondary, fontFamily: attendanceFontFamily, fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.25, textAlign: 'center' }}>
        Not Scheduled
      </Typography>
    </Box>
  )
}

function DateHeaderCell (props: {
  bulkActionsDisabled: boolean
  currentDate: string
  date: AttendanceDateColumnDto
  readOnly: boolean
  rows: AttendanceRosterRowDto[]
  onBulkMenuOpen: (anchor: HTMLElement) => void
}): JSX.Element {
  const editableCellCount = editableCellsForDate(props.rows, props.date).length
  const clearableCellCount = clearableCellsForDate(props.rows, props.date).length
  const actionCellCount = editableCellCount + clearableCellCount
  const hasEditableExceptionAction = props.date.canMarkNotInSession === true ||
    props.date.canClearNotInSession === true ||
    props.date.canEditNotInSessionReason === true ||
    props.date.showNotInSessionAction === true
  const hasNormalBulkAction = props.date.canEdit && actionCellCount > 0
  const hasExceptionMenuAction = props.date.isNotInSession === true ||
    props.date.canMarkNotInSession === true ||
    props.date.showNotInSessionAction === true
  const showActionButton = !props.readOnly || hasEditableExceptionAction
  const disabled = props.date.isScheduled === false ||
    props.bulkActionsDisabled ||
    (!hasNormalBulkAction && !hasExceptionMenuAction)

  return (
    <HeaderCell
      sx={{
        ...columnWidthSx(dateColumnWidth),
        ...currentColumnSx(isCurrentDate(props.date.attendanceDate, props.currentDate)),
        px: 1.5
      }}
    >
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, justifyContent: 'space-between' }}>
        <Box>
          <Typography sx={{ color: textPrimary, fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.2 }}>{weekdayLabel(props.date.attendanceDate)}</Typography>
          <Typography sx={{ color: textSecondary, fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.2 }}>{monthDayLabel(props.date.attendanceDate)}</Typography>
        </Box>
        {showActionButton && (
          <Tooltip title={bulkTooltipTitle(props.date, props.bulkActionsDisabled, actionCellCount)}>
            <span>
              <IconButton
                aria-label={`Attendance actions for ${weekdayLabel(props.date.attendanceDate)} ${monthDayLabel(props.date.attendanceDate)}`}
                data-testid={`attendance-bulk-menu-${dateKey(props.date.attendanceDate)}`}
                disabled={disabled}
                size="small"
                onClick={event => { props.onBulkMenuOpen(event.currentTarget) }}
                sx={{
                  color: textSecondary,
                  height: 28,
                  width: 28
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    </HeaderCell>
  )
}

function tableMinWidth (dateCount: number): number {
  return studentColumnWidth + attendanceRateColumnWidth + tardyColumnWidth + dateColumnWidth * dateCount
}

function columnWidthSx (width: number): { maxWidth: number, minWidth: number, width: number } {
  return {
    maxWidth: width,
    minWidth: width,
    width
  }
}

function HeaderCell (props: React.ComponentProps<typeof TableCell>): JSX.Element {
  return (
    <TableCell
      {...props}
      sx={[{
        backgroundColor: '#FFFFFF',
        borderColor: '#E6E6E6',
        color: textPrimary,
        fontFamily: attendanceFontFamily,
        fontSize: '0.75rem',
        fontWeight: 500,
        py: 1.5
      }, ...(Array.isArray(props.sx) ? props.sx : [props.sx])]}
    />
  )
}

function SortableHeaderLabel (props: {
  active: boolean
  direction: SortDirection
  label: string
  onClick: () => void
}): JSX.Element {
  return (
    <TableSortLabel
      active={props.active}
      direction={props.direction}
      onClick={props.onClick}
      sx={{
        color: textPrimary,
        fontFamily: attendanceFontFamily,
        fontSize: '0.75rem',
        fontWeight: 500,
        '&.Mui-active': {
          color: textPrimary
        },
        '& .MuiTableSortLabel-icon': {
          color: `${textMuted} !important`,
          fontSize: 18
        }
      }}
    >
      {props.label}
    </TableSortLabel>
  )
}

function BodyCell (props: React.ComponentProps<typeof TableCell>): JSX.Element {
  return (
    <TableCell
      {...props}
      sx={[{
        borderColor: '#E6E6E6',
        py: 1.5,
        verticalAlign: 'middle'
      }, ...(Array.isArray(props.sx) ? props.sx : [props.sx])]}
    />
  )
}

const menuItemSx = {
  color: textPrimary,
  fontFamily: attendanceFontFamily,
  fontSize: '0.875rem',
  fontWeight: 500,
  minHeight: 36
}

function cellTooltipTitle (cell: AttendanceCellDto, saving: boolean): string {
  if (saving) return 'Attendance is saving.'
  if (cell.willLockAfterSubmit === true && cell.status == null) return 'This status will lock after it is submitted.'
  if (cell.status != null && !cell.canEdit) return 'Submitted attendance statuses can only be changed during this week or last week.'
  if (!cell.canEdit) return 'Attendance cannot be edited for this date.'
  return ''
}

function bulkTooltipTitle (date: AttendanceDateColumnDto, bulkActionsDisabled: boolean, editableCellCount: number): string {
  if (date.isScheduled === false) return 'This day is not scheduled for this session.'
  if (bulkActionsDisabled) return 'Finish the current save before using bulk actions.'
  if (date.isNotInSession === true) return date.canClearNotInSession === true ? 'Not-in-session day actions' : 'This not-in-session day is locked.'
  if (date.canMarkNotInSession === true) return 'Day attendance actions'
  if (date.notInSessionActionDisabledReason != null && date.notInSessionActionDisabledReason !== '') return 'Day attendance actions'
  if (!date.canEdit) return 'Attendance cannot be edited for this date.'
  if (editableCellCount === 0) return 'There are no editable attendance statuses for this date.'
  return 'Day attendance actions'
}

function currentColumnSx (isCurrent: boolean): { backgroundColor?: string, borderLeft?: string, borderRight?: string } {
  if (!isCurrent) return {}

  return {
    backgroundColor: '#EAF7FC',
    borderLeft: '1px solid #C7E8F3',
    borderRight: '1px solid #C7E8F3'
  }
}

function editableCellsForDate (rows: AttendanceRosterRowDto[], date: AttendanceDateColumnDto, status?: AttendanceRecordStatus): Array<{ row: AttendanceRosterRowDto, cell: AttendanceCellDto }> {
  if (!date.isInSession || date.isNotInSession === true || !date.canEdit) return []

  return rows
    .map(row => ({ row, cell: cellForDate(row, date.attendanceDate) }))
    .filter((entry): entry is { row: AttendanceRosterRowDto, cell: AttendanceCellDto } =>
      entry.cell?.canEdit === true && (status === undefined || entry.cell.status !== status))
}

function clearableCellsForDate (rows: AttendanceRosterRowDto[], date: AttendanceDateColumnDto): Array<{ row: AttendanceRosterRowDto, cell: AttendanceCellDto }> {
  if (!date.isInSession || date.isNotInSession === true || !date.canEdit) return []

  return rows
    .map(row => ({ row, cell: cellForDate(row, date.attendanceDate) }))
    .filter((entry): entry is { row: AttendanceRosterRowDto, cell: AttendanceCellDto } =>
      entry.cell?.canEdit === true && entry.cell.status != null && entry.cell.attendanceRecordId != null)
}

function sortedRows (rows: AttendanceRosterRowDto[], sort: { column: SortColumn, direction: SortDirection } | null): AttendanceRosterRowDto[] {
  if (sort == null) return rows

  return [...rows].sort((left, right) => {
    const leftValue = rateSortValue(left[sort.column])
    const rightValue = rateSortValue(right[sort.column])
    if (leftValue === rightValue) return left.childName.localeCompare(right.childName)
    return sort.direction === 'asc' ? leftValue - rightValue : rightValue - leftValue
  })
}

function rateSortValue (value: number | null | undefined): number {
  return value ?? -1
}

function attendanceRateColor (rate: number | null | undefined): string {
  if (rate == null || rate > 0.95) return textPrimary
  if (rate >= 0.9) return '#7136BC'
  return '#982A43'
}

function isCurrentDate (attendanceDate: string, currentDate: string): boolean {
  return dateKey(attendanceDate) === dateKey(currentDate)
}

function todayDateValue (): string {
  const today = new Date()
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}T00:00:00`
}

function cellForDate (row: AttendanceRosterRowDto, attendanceDate: string): AttendanceCellDto | undefined {
  return row.cells.find(cell => dateKey(cell.attendanceDate) === dateKey(attendanceDate))
}

function hasNoteCapableAbsenceWithoutNote (row: AttendanceRosterRowDto): boolean {
  return row.cells.some(cell => cell.status === 'Absent' && cell.canAddNote && (cell.note == null || cell.note.trim() === ''))
}

export function cellKey (newEnrollmentId: number, attendanceDate: string): string {
  return `${newEnrollmentId}-${dateKey(attendanceDate)}`
}

function dateKey (value: string): string {
  return value.substring(0, 10)
}

function parseDateOnly (value: string): Date {
  const [year, month, day] = dateKey(value).split('-').map(Number)
  return new Date(year, month - 1, day)
}

function weekdayLabel (value: string): string {
  return parseDateOnly(value).toLocaleDateString('en-US', { weekday: 'long' })
}

function monthDayLabel (value: string): string {
  return parseDateOnly(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
